import { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { useSeedMatchScore } from '../../api/useMatchEvents';
import { buildSeedDto, byHalfHasScore, num } from '../../utils/seedScore';

const digits = (v: string) => v.replace(/[^0-9]/g, '');

/** Compact single-digit-ish numeric input shared across the control. */
function Num({
  label,
  value,
  onChange,
  ariaLabel,
  width = 44,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  width?: number;
}) {
  return (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(digits(e.target.value))}
      inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': ariaLabel }}
      sx={{
        width,
        '& .MuiInputBase-input': { py: 0.35, px: 0.5, fontSize: 12, textAlign: 'center' },
        '& .MuiInputLabel-root': { fontSize: 10.5 },
        '& .MuiInputLabel-shrink': { fontSize: 11 },
      }}
    />
  );
}

const dash = (
  <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>
    –
  </Typography>
);

/**
 * Operator "set score" control for establishing (or correcting) the backend
 * `MatchState` baseline when the LSports feed cold-started mid-match and no
 * authoritative score exists yet (the `Incidents` feed has no replay/backfill on
 * connect, so a pre-connect goal is never re-sent). It POSTs to
 * `POST /match-events/matches/:fixtureId/seed-score`; the engine interprets the
 * seed and re-publishes the authoritative `MatchState` over the `match_state` WS
 * event — the FE renders that backend state, NEVER the typed values directly.
 *
 * Two entry modes:
 * - **Global** (default): the MATCH TOTAL. LSports scores per period, so a global
 *   seed can't know the current period's split — the engine leaves an anchor that
 *   absorbs the first post-seed reading (a goal in that window is folded into the
 *   total). Fine when the match just started / you only know the total.
 * - **By half** (`Por tiempo`): the exact 1H/2H breakdown. The engine seeds each
 *   period bucket directly — no anchor, so every later goal counts. The global
 *   total is derived from the halves.
 *
 * `mode: 'seed'` (no baseline yet) opens the inputs by default. `mode: 'override'`
 * (a baseline exists — the feed can mis-state a score) starts collapsed behind a
 * "Correct score" button. Corners are optional; scores are required. Failures
 * surface through the global mutation-error toast (the mutation carries a
 * `mutationKey`) — never a silent failure.
 */
export function SeedScoreControl({
  fixtureId,
  homeTeam,
  awayTeam,
  mode,
}: {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  mode: 'seed' | 'override';
}) {
  const seed = useSeedMatchScore();
  const [open, setOpen] = useState(mode === 'seed');
  const [byHalf, setByHalf] = useState(false);

  // Global (total) fields.
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [homeCorners, setHomeCorners] = useState('');
  const [awayCorners, setAwayCorners] = useState('');

  // Per-half fields — blank, treated as 0 by `buildSeedDto`. Kept blank (not '0')
  // so an accidental "Set" the instant "Por tiempo" is flipped can't clobber a
  // live baseline with 0–0: `canSubmit` requires at least one SCORE field typed
  // (`byHalfHasScore` — a corner alone won't do it), and an explicit 0–0 seed
  // still works by typing a `0`.
  const [h1, setH1] = useState('');
  const [a1, setA1] = useState('');
  const [h2, setH2] = useState('');
  const [a2, setA2] = useState('');
  const [ch1, setCh1] = useState('');
  const [ca1, setCa1] = useState('');
  const [ch2, setCh2] = useState('');
  const [ca2, setCa2] = useState('');

  const canSubmit = seed.isLoading
    ? false
    : byHalf
      ? byHalfHasScore({ h1, a1, h2, a2 })
      : home !== '' && away !== '';

  const reset = () => {
    setHome('');
    setAway('');
    setHomeCorners('');
    setAwayCorners('');
    setH1('');
    setA1('');
    setH2('');
    setA2('');
    setCh1('');
    setCa1('');
    setCh2('');
    setCa2('');
    setByHalf(false);
  };

  const submit = () => {
    if (!canSubmit) return;
    const dto = buildSeedDto({
      byHalf,
      home,
      away,
      homeCorners,
      awayCorners,
      h1,
      a1,
      h2,
      a2,
      ch1,
      ca1,
      ch2,
      ca2,
    });
    seed.mutate(
      { fixtureId, dto },
      {
        onSuccess: () => {
          reset();
          // A corrected baseline collapses back; a fresh seed leaves the inputs
          // open until the backend state arrives and swaps this out for the board.
          if (mode === 'override') setOpen(false);
        },
      },
    );
  };

  if (!open) {
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<SportsScoreIcon fontSize="small" />}
        onClick={() => setOpen(true)}
      >
        Correct score
      </Button>
    );
  }

  const derivedTotal = byHalf
    ? `${num(h1) + num(h2)}–${num(a1) + num(a2)}`
    : '';

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Set score
        </Typography>

        {byHalf ? (
          <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 600 }}>
            {derivedTotal}
            <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
              (de los tiempos)
            </Box>
          </Typography>
        ) : (
          <>
            <Num label={homeTeam || 'H'} value={home} onChange={setHome} ariaLabel="home score" width={54} />
            {dash}
            <Num label={awayTeam || 'A'} value={away} onChange={setAway} ariaLabel="away score" width={54} />
            <Num label="🚩H" value={homeCorners} onChange={setHomeCorners} ariaLabel="home corners" />
            <Num label="🚩A" value={awayCorners} onChange={setAwayCorners} ariaLabel="away corners" />
          </>
        )}

        <FormControlLabel
          sx={{ ml: 0.5, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 11 } }}
          control={
            <Switch
              size="small"
              checked={byHalf}
              onChange={(e) => setByHalf(e.target.checked)}
              inputProps={{ 'aria-label': 'seed by half' }}
            />
          }
          label="Por tiempo"
        />

        <Button size="small" variant="contained" onClick={submit} disabled={!canSubmit} sx={{ minWidth: 0, px: 1.25 }}>
          {seed.isLoading ? '…' : 'Set'}
        </Button>
        {mode === 'override' && (
          <Button
            size="small"
            onClick={() => {
              reset();
              setOpen(false);
            }}
            disabled={seed.isLoading}
            sx={{ minWidth: 0, px: 1 }}
          >
            Cancel
          </Button>
        )}
      </Stack>

      {byHalf && (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            1H
          </Typography>
          <Num label="H" value={h1} onChange={setH1} ariaLabel="1H home score" />
          {dash}
          <Num label="A" value={a1} onChange={setA1} ariaLabel="1H away score" />
          <Num label="🚩H" value={ch1} onChange={setCh1} ariaLabel="1H home corners" />
          <Num label="🚩A" value={ca1} onChange={setCa1} ariaLabel="1H away corners" />

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, ml: 1 }}>
            2H
          </Typography>
          <Num label="H" value={h2} onChange={setH2} ariaLabel="2H home score" />
          {dash}
          <Num label="A" value={a2} onChange={setA2} ariaLabel="2H away score" />
          <Num label="🚩H" value={ch2} onChange={setCh2} ariaLabel="2H home corners" />
          <Num label="🚩A" value={ca2} onChange={setCa2} ariaLabel="2H away corners" />
        </Stack>
      )}
    </Stack>
  );
}
